// Additive store: never upgrades, rewrites or deletes legacy delivery-outbox data.
import { copyDeliveryPhotoAttempt, selectDeliveryPhotoBytes } from './deliveryPhotoSubmission.js';

const DATABASE = 'lvb-delivery-photo-drafts';
const STORE = 'drafts';
const ALTERNATIVES = 'alternatives';
const RESOLUTIONS = 'resolutions';
const DATABASE_VERSION = 2;
const RECORD_VERSION = 4;
const compatible = (version) => [1, 2, 3, RECORD_VERSION].includes(version);
const matchesKind = (attempt, kind) => !attempt || attempt.kind === kind || (kind === 'confirmation' && attempt.kind === 'failure');
const conflict = () => Object.assign(new Error('Saved copies need recovery review; nothing was overwritten. Keep this tab open if its changes are not saved.'), { code: 'PHOTO_DRAFT_CONFLICT' });

export function photoDraftKey(principal, orderId, kind) {
  let parts;
  try { parts = JSON.parse(principal); } catch { /* Refuse unowned records. */ }
  if (!Array.isArray(parts) || parts.length !== 3 || parts.slice(0, 2).some((p) => typeof p !== 'string' || !p.trim())
      || !['cloud', 'offline'].includes(parts[2]) || typeof orderId !== 'string' || !orderId.trim()
      || !['confirmation', 'amendment'].includes(kind)) throw new Error('A current account and order are required for photo recovery.');
  return JSON.stringify([principal, orderId, kind]);
}

function open() {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) { reject(new Error('Device storage is unavailable.')); return; }
    const request = indexedDB.open(DATABASE, DATABASE_VERSION);
    let refused = false;
    request.onupgradeneeded = () => {
      [STORE, ALTERNATIVES, RESOLUTIONS].forEach((name) => {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name, { keyPath: 'key' });
      });
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => { refused = true; reject(new Error('Device storage is busy in another tab.')); };
    request.onsuccess = () => {
      if (refused) { request.result.close(); return; }
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
  });
}

// Explicit facts only; no credentials, object URLs or authority are persisted.
export function persistentPhotoFields(fields) {
  if ((fields.failureReason != null && (typeof fields.failureReason !== 'string' || !['', 'nobody_home', 'wrong_address', 'other'].includes(fields.failureReason)))
      || (fields.failureDetails != null && typeof fields.failureDetails !== 'string')) throw new Error('Saved failure details need review.');
  const photos = (fields.photos || []).map((photo) => {
    if (!(photo.file instanceof Blob)) throw new Error('Missing original photo; retain this draft for review.');
    return {
      file: photo.file,
      prepared: photo.prepared ? { file: photo.prepared.file, width: photo.prepared.width, height: photo.prepared.height, policy: photo.prepared.policy } : null,
      preparationError: photo.preparationError || null,
      useOriginal: photo.useOriginal === true,
    };
  });
  return {
    failureReason: fields.failureReason || '', failureDetails: fields.failureDetails || '',
    receivedBy: fields.receivedBy || '', receivedByRelationship: fields.receivedByRelationship || '', notes: fields.notes || '',
    deliveryTime: fields.deliveryTime || '', deliveryTimeIso: fields.deliveryTimeIso,
    signatureData: fields.signatureData || null,
    gpsLocation: fields.gpsLocation ? { latitude: fields.gpsLocation.latitude, longitude: fields.gpsLocation.longitude, accuracy: fields.gpsLocation.accuracy } : null,
    gpsStatus: fields.gpsStatus || 'pending', gpsError: fields.gpsError || null,
    locationCleared: fields.locationCleared === true,
    video: fields.video ? { file: fields.video.file } : null, photos,
  };
}

export function persistentPhotoRecovery(value) {
  if (value == null) return { attempt: null, checkpoint: null };
  const attempt = copyDeliveryPhotoAttempt(value.attempt);
  const revision = value.checkpoint?.amendmentRevision;
  if (revision !== undefined && !/^[a-f0-9]{64}$/.test(revision || '')) throw new Error('Saved delivery revision needs review.');
  const copyText = (text) => {
    if (!text) return undefined;
    if (['received_by', 'received_by_relationship', 'notes'].some((key) => typeof text[key] !== 'string')) {
      throw new Error('Saved amendment checkpoint needs review.');
    }
    return { received_by: text.received_by, received_by_relationship: text.received_by_relationship, notes: text.notes };
  };
  return { attempt, checkpoint: value.checkpoint ? {
    baselineText: copyText(value.checkpoint.baselineText), acknowledgedText: copyText(value.checkpoint.acknowledgedText),
    ...(revision === undefined ? {} : { amendmentRevision: revision }),
  } : null };
}

export function restorePhotoRecovery(value) {
  const facts = persistentPhotoRecovery(value);
  // A browser/process restart cannot prove a pending write did not arrive.
  return { ...facts, attempt: facts.attempt?.status === 'pending' ? { ...facts.attempt, status: 'unknown' } : facts.attempt };
}

export function restorePhotoFields(fields) {
  const facts = persistentPhotoFields(fields);
  const created = [];
  const url = (blob) => { const result = URL.createObjectURL(blob); created.push(result); return result; };
  try {
    return { ...facts,
      photos: facts.photos.map((photo) => ({ ...photo, preview: url(photo.prepared?.file || photo.file) })),
      video: facts.video ? { ...facts.video, preview: url(facts.video.file) } : null,
    };
  } catch (error) { created.forEach((value) => URL.revokeObjectURL(value)); throw error; }
}

const resolutionKey = (principal, orderId) => JSON.stringify([principal, orderId]);
const copyId = (record) => record.copyId || `device:${record.revision}`;
const validateRecord = (record, key) => {
  if (!record) return null;
  if (!compatible(record.version) || record.key !== key || typeof record.revision !== 'string') throw new Error('This saved draft needs a compatible app; nothing was changed.');
  persistentPhotoRecovery(record.recovery);
  if (record.source === 'legacy-failure' && (record.kind !== 'confirmation' || typeof record.legacyAttempted !== 'boolean' || record.recovery?.attempt)) throw new Error('Saved older failure needs review.');
  return record;
};

const phaseNames = (kind) => kind === 'confirmation' ? ['core', 'video'] : kind === 'amendment' ? ['text', 'media'] : [];
function validateResolution(resolution, key) {
  if (!resolution) return;
  if (resolution.version !== 1 || resolution.key !== key || typeof resolution.revision !== 'string'
      || !Array.isArray(resolution.evidence) || !resolution.selections || Array.isArray(resolution.selections)
      || (resolution.legacyFailureHeld !== undefined && typeof resolution.legacyFailureHeld !== 'boolean')
      || Object.entries(resolution.selections).some(([operation, selected]) => !['confirmation', 'amendment'].includes(operation) || (selected !== null && typeof selected !== 'string'))
      || resolution.evidence.some((item) => !item || !['confirmation', 'amendment', 'failure'].includes(item.kind)
        || !/^[A-Za-z0-9_-]{1,128}$/.test(item.captureId || '') || typeof item.accepted !== 'boolean'
        || (item.abandoned !== undefined && typeof item.abandoned !== 'boolean')
        || (item.mediaRevision !== null && !/^[a-f0-9]{64}$/.test(item.mediaRevision || ''))
        || (item.receiptPhases !== undefined && (!item.receiptPhases || Array.isArray(item.receiptPhases)
          || Object.keys(item.receiptPhases).some((name) => !phaseNames(item.kind).includes(name))
          || phaseNames(item.kind).some((name) => typeof item.receiptPhases[name] !== 'boolean'))))
      || new Set(resolution.evidence.map(attemptKey)).size !== resolution.evidence.length) throw new Error('Saved recovery needs a compatible app.');
}

// All copy and resolution reads share the mutation transaction. Enumerate keys
// before values so another account's media is never loaded for this decision.
async function recoveryTransaction(principal, orderId, kind, mode, decide) {
  const activeKey = photoDraftKey(principal, orderId, kind);
  const ownerKey = resolutionKey(principal, orderId);
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE, ALTERNATIVES, RESOLUTIONS], mode);
      const stores = Object.fromEntries([STORE, ALTERNATIVES, RESOLUTIONS].map((name) => [name, tx.objectStore(name)]));
      let result = null; let failure; let waiting = 3;
      const records = []; const alternatives = []; let resolution = null;
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(failure || tx.error || new Error('Draft save was not committed.'));
      tx.onerror = () => reject(failure || tx.error || new Error('Draft storage failed.'));
      const fail = (error) => { failure = error; tx.abort(); };
      const finish = () => {
        if (--waiting) return;
        try {
          validateResolution(resolution, ownerKey);
          const found = records.find((record) => record.key === activeKey);
          const state = { active: found ? { ...found, copyId: copyId(found) } : null,
            alternatives: alternatives.filter((record) => record.kind === kind), resolution };
          const meta = { stores, records, alternatives, ownerKey, activeKey };
          const evidence = collectEvidence(state, meta);
          state.active = applyEvidence(state.active, evidence);
          state.alternatives = state.alternatives.map((record) => applyEvidence(record, evidence));
          result = decide ? decide(state, meta) : state;
        } catch (error) { fail(error); }
      };
      const scan = (name, output) => {
        const cursor = stores[name].openKeyCursor();
        cursor.onsuccess = () => {
          const entry = cursor.result;
          if (!entry) { finish(); return; }
          let parts;
          try { parts = JSON.parse(entry.key); } catch { entry.continue(); return; }
          if (!Array.isArray(parts) || parts[0] !== principal || parts[1] !== orderId) { entry.continue(); return; }
          const request = stores[name].get(entry.key);
          request.onsuccess = () => {
            try {
              const record = validateRecord(request.result, entry.key);
              if (!record || !['confirmation', 'amendment'].includes(parts[2])
                  || parts.length !== (name === STORE ? 3 : 4) || !matchesKind(record.recovery?.attempt, parts[2])
                  || (name === ALTERNATIVES && (record.kind !== parts[2] || record.copyId !== parts[3]))) throw new Error('Saved copy identity needs review.');
              output.push(record); entry.continue();
            } catch (error) { fail(error); }
          };
        };
      };
      scan(STORE, records); scan(ALTERNATIVES, alternatives);
      const readResolution = stores[RESOLUTIONS].get(ownerKey);
      readResolution.onsuccess = () => { resolution = readResolution.result || null; finish(); };
    });
  } finally { db.close(); }
}

const attemptKey = (attempt) => `${attempt.kind}:${attempt.captureId}`;
const attemptSignature = (attempt) => JSON.stringify({ kind: attempt.kind, captureId: attempt.captureId,
  fields: attempt.fields, manifest: attempt.manifest, expectedRevision: attempt.amendment?.expectedRevision, textRequired: attempt.amendment?.textRequired });
function collectEvidence(state, meta, extraRecords = []) {
  const evidence = new Map((state.resolution?.evidence || []).map((item) => [attemptKey(item), { ...item }]));
  const identities = new Map();
  [...meta.records, ...meta.alternatives, ...extraRecords].forEach((record) => {
    const attempt = persistentPhotoRecovery(record.recovery).attempt;
    if (!attempt) return;
    const identity = attemptSignature(attempt);
    if (identities.has(attemptKey(attempt)) && identities.get(attemptKey(attempt)) !== identity) throw conflict();
    identities.set(attemptKey(attempt), identity);
    const previous = evidence.get(attemptKey(attempt));
    const mediaRevision = attempt.amendment?.mediaRevision || null;
    if (previous?.mediaRevision && mediaRevision && previous.mediaRevision !== mediaRevision) throw conflict();
    const phases = attempt.receiptPhases || (attempt.status === 'accepted' ? (attempt.kind === 'confirmation'
      ? { core: true, video: !!attempt.manifest.video } : attempt.kind === 'amendment'
        ? { text: attempt.amendment.textRequired, media: !!(attempt.manifest.photos.length || attempt.manifest.video) } : null) : null);
    const receiptPhases = previous?.receiptPhases || phases ? Object.fromEntries(phaseNames(attempt.kind)
      .map((name) => [name, previous?.receiptPhases?.[name] === true || phases?.[name] === true])) : null;
    evidence.set(attemptKey(attempt), { ...previous, kind: attempt.kind, captureId: attempt.captureId,
      accepted: previous?.accepted === true || attempt.status === 'accepted', mediaRevision: previous?.mediaRevision || mediaRevision,
      ...(receiptPhases ? { receiptPhases } : {}) });
  });
  return [...evidence.values()];
}

function applyEvidence(record, evidence) {
  const attempt = record?.recovery?.attempt;
  if (!attempt) return record;
  const known = evidence.find((item) => attemptKey(item) === attemptKey(attempt));
  if (!known) return record;
  const updated = copyDeliveryPhotoAttempt({ ...attempt, status: known.accepted ? 'accepted' : attempt.status,
    ...(known.receiptPhases ? { receiptPhases: known.receiptPhases } : {}),
    ...(attempt.amendment && known.mediaRevision ? { amendment: { ...attempt.amendment, mediaRevision: known.mediaRevision } } : {}) });
  return { ...record, recovery: { ...record.recovery, attempt: updated } };
}

function nextResolution(state, meta, extraRecords = []) {
  return { key: meta.ownerKey, version: 1, revision: crypto.randomUUID(),
    selections: { ...state.resolution?.selections }, evidence: collectEvidence(state, meta, extraRecords),
    ...((state.resolution?.legacyFailureHeld || [...meta.alternatives, ...extraRecords].some((record) => record.legacyAttempted)) ? { legacyFailureHeld: true } : {}) };
}

async function transact(key, mode, change) {
  const [principal, orderId, kind] = JSON.parse(key);
  return recoveryTransaction(principal, orderId, kind, mode, (state, meta) => change
    ? change(meta.stores[STORE], state.active, state, meta) : state.active);
}

export const loadPhotoDraft = (principal, orderId, kind) => transact(photoDraftKey(principal, orderId, kind), 'readonly');
export const savePhotoDraft = (principal, orderId, kind, fields, expectedRevision, recovery, { isCurrent = () => true } = {}) => {
  const key = photoDraftKey(principal, orderId, kind);
  const facts = persistentPhotoFields(fields);
  return transact(key, 'readwrite', (store, current, state, meta) => {
    if (!isCurrent()) throw new Error('Draft opening is no longer active. Nothing was saved.');
    if ((current?.revision ?? null) !== expectedRevision) throw conflict();
    if (state.resolution?.selections[kind] === null || state.resolution?.legacyFailureHeld
        || meta.alternatives.some((record) => record.source === 'legacy-failure')) throw conflict();
    // Omitted recovery means preserve, not clear. Old field-only writers must
    // never erase a frozen attempt or an acknowledged amendment step.
    const retained = persistentPhotoRecovery(recovery === undefined ? current?.recovery : recovery);
    if (!matchesKind(retained.attempt, kind)) throw new Error('Saved attempt belongs to a different delivery operation.');
    const prior = current?.recovery?.attempt;
    if (prior && (!retained.attempt || attemptKey(prior) !== attemptKey(retained.attempt))) throw conflict();
    const evidence = nextResolution(state, meta).evidence;
    if (evidence.some((item) => (item.abandoned && retained.attempt && attemptKey(item) === attemptKey(retained.attempt))
        || (!item.accepted && !item.abandoned && (!retained.attempt || attemptKey(item) !== attemptKey(retained.attempt)))
        || (item.accepted && retained.attempt && attemptKey(item) === attemptKey(retained.attempt) && retained.attempt.status !== 'accepted')
        || (retained.attempt && attemptKey(item) === attemptKey(retained.attempt) && retained.attempt.status !== 'accepted'
          && Object.entries(item.receiptPhases || {}).some(([phase, accepted]) => accepted && !retained.attempt.receiptPhases?.[phase]))
        || (item.mediaRevision && retained.attempt && attemptKey(item) === attemptKey(retained.attempt) && retained.attempt.amendment?.mediaRevision !== item.mediaRevision))) throw conflict();
    const revision = crypto.randomUUID();
    // A save changes snapshot facts. Do not keep a former tab snapshot identity
    // attached to changed contents; selection alone preserves the existing ID.
    const value = { key, version: RECORD_VERSION, revision, copyId: `device:${revision}`, fields: facts, recovery: retained };
    store.put(value);
    const resolution = nextResolution(state, meta, [value]);
    if (Object.hasOwn(resolution.selections, kind)) resolution.selections[kind] = value.copyId;
    meta.stores[RESOLUTIONS].put(resolution);
    if (!isCurrent()) throw new Error('Draft opening is no longer active. Nothing was saved.');
    return value;
  });
};

export const discardPhotoDraft = (principal, orderId, kind, expectedRevision, { unsentOnly = false, acceptedFailureOnly = false, verified = false, isCurrent = () => true } = {}) => verified
  ? retireVerifiedPhotoCopies(principal, orderId, kind, { expectedCopyRevision: expectedRevision, isCurrent }) : transact(
  photoDraftKey(principal, orderId, kind), 'readwrite', (store, current, state, meta) => {
    if (!isCurrent()) throw new Error('Draft selection is no longer active. Nothing was discarded.');
    if ((current?.revision ?? null) !== expectedRevision) throw conflict();
    if (state.resolution?.selections[kind] === null) throw conflict();
    if (unsentOnly && current?.recovery?.attempt) throw new Error('Check the saved attempt receipt before retiring this work.');
    if (acceptedFailureOnly && (current?.recovery?.attempt?.kind !== 'failure' || current.recovery.attempt.status !== 'accepted')) throw new Error('Verify failure acceptance before discarding its local photos.');
    if (current?.recovery?.attempt && current.recovery.attempt.status !== 'accepted') throw conflict();
    const resolution = nextResolution(state, meta);
    delete resolution.selections[kind];
    meta.stores[RESOLUTIONS].put(resolution);
    if (current) store.delete(current.key);
    return null;
  },
);

export const readPhotoRecovery = (principal, orderId, kind) => recoveryTransaction(principal, orderId, kind, 'readonly');

// This inspection deliberately never fetches draft/alternative values. Legacy
// admission needs a hold, not permission to read another account's media/text.
async function inspectPhotoOrderHold(orderId, excludePrincipal) {
  if (typeof orderId !== 'string' || !orderId.trim()) throw new Error('An order is required for saved-work admission.');
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE, ALTERNATIVES, RESOLUTIONS], 'readonly');
      let hasCopies = false; let hasAttemptEvidence = false; let failure;
      tx.oncomplete = () => resolve({ held: hasCopies || hasAttemptEvidence, hasCopies, hasAttemptEvidence });
      tx.onabort = () => reject(failure || tx.error || new Error('Saved-work admission could not be checked.'));
      tx.onerror = () => reject(failure || tx.error || new Error('Saved-work admission could not be checked.'));
      [STORE, ALTERNATIVES, RESOLUTIONS].forEach((name) => {
        const store = tx.objectStore(name); const cursor = store.openKeyCursor();
        cursor.onsuccess = () => {
          const entry = cursor.result;
          if (!entry) return;
          let parts;
          try { parts = JSON.parse(entry.key); } catch { entry.continue(); return; }
          if (!Array.isArray(parts) || parts[1] !== orderId || parts[0] === excludePrincipal) { entry.continue(); return; }
          if (name !== RESOLUTIONS) { hasCopies = true; entry.continue(); return; }
          const read = store.get(entry.key);
          read.onsuccess = () => {
            try {
              if (parts.length !== 2 || !read.result) throw new Error('Saved-work hold needs a compatible app.');
              validateResolution(read.result, entry.key);
              hasAttemptEvidence ||= !!(read.result.evidence.length || read.result.legacyFailureHeld);
              entry.continue();
            } catch (error) { failure = error; tx.abort(); }
          };
        };
      });
    });
  } finally { db.close(); }
}

export const readPhotoOrderHold = (orderId) => inspectPhotoOrderHold(orderId);

// Caller holds the shared order lock and separately checks current legacy data
// and server authority. This is a local identity gate, never permission itself.
export async function assertPhotoDispatch(principal, orderId, kind, captureId) {
  const storageKind = kind === 'failure' ? 'confirmation' : kind;
  photoDraftKey(principal, orderId, storageKind);
  if (typeof captureId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(captureId)) throw conflict();
  if ((await inspectPhotoOrderHold(orderId, principal)).held) throw conflict();
  return recoveryTransaction(principal, orderId, storageKind, 'readonly', (state, meta) => {
    const attempt = state.active?.recovery?.attempt;
    if (!attempt || attempt.kind !== kind || attempt.captureId !== captureId || attempt.status === 'accepted'
        || state.resolution?.selections[storageKind] === null || state.resolution?.legacyFailureHeld
        || meta.alternatives.some((record) => record.source === 'legacy-failure')) throw conflict();
    if (collectEvidence(state, meta).some((item) => item.abandoned ? attemptKey(item) === attemptKey(attempt)
      : !item.accepted && attemptKey(item) !== attemptKey(attempt))) throw conflict();
    return true;
  });
}

// Outcome must come from the exact strict receipt reader for this stored
// attempt. Revalidate identity and frozen facts here; only evidence is adopted,
// never caller-provided media/fields, and a check never deletes a copy.
export function recordPhotoReceipt(principal, orderId, kind, options) {
  return recoveryTransaction(principal, orderId, kind, 'readwrite', (state, meta) => {
    const { expectedActiveRevision, expectedResolutionRevision, expectedCopyRevision, outcome, isCurrent = () => true } = options;
    if (!isCurrent()) throw new Error('Receipt review is no longer active. Nothing was changed.');
    if ((state.active?.revision ?? null) !== expectedActiveRevision || (state.resolution?.revision ?? null) !== expectedResolutionRevision) throw conflict();
    const id = options.copyId === 'active' ? state.active?.copyId : options.copyId;
    const selected = state.active?.copyId === id ? state.active : state.alternatives.find((record) => record.copyId === id);
    if (!selected || selected.revision !== expectedCopyRevision || !selected.recovery?.attempt) throw conflict();
    const previous = copyDeliveryPhotoAttempt(selected.recovery.attempt);
    const observed = copyDeliveryPhotoAttempt(outcome?.attempt);
    if (!observed || attemptSignature(observed) !== attemptSignature(previous)) throw conflict();
    let expectedState;
    if (observed.kind === 'failure') {
      if (!['accepted', 'not-found'].includes(outcome.state)) throw conflict();
      expectedState = outcome.state;
    } else if (observed.kind === 'confirmation') {
      if (!observed.receiptPhases) throw conflict();
      expectedState = !observed.receiptPhases.core ? 'not-found' : observed.manifest.video && !observed.receiptPhases.video ? 'video-outstanding' : 'accepted';
    } else {
      if (!observed.receiptPhases) throw conflict();
      expectedState = observed.amendment.textRequired && !observed.receiptPhases.text ? 'text-outstanding'
        : (observed.manifest.photos.length || observed.manifest.video) && !observed.receiptPhases.media ? 'media-outstanding' : 'accepted';
    }
    if (outcome.state !== expectedState || (previous.status === 'accepted' && expectedState !== 'accepted')
        || Object.entries(previous.receiptPhases || {}).some(([phase, accepted]) => accepted && !observed.receiptPhases?.[phase])
        || (previous.amendment?.mediaRevision && previous.amendment.mediaRevision !== observed.amendment?.mediaRevision)) throw conflict();
    const attempt = copyDeliveryPhotoAttempt({ ...previous, status: expectedState === 'accepted' ? 'accepted' : 'unknown',
      ...(observed.receiptPhases ? { receiptPhases: observed.receiptPhases } : {}),
      ...(observed.amendment ? { amendment: observed.amendment } : {}) });
    const updated = { ...selected, version: RECORD_VERSION, revision: crypto.randomUUID(), recovery: { ...selected.recovery, attempt } };
    const resolution = nextResolution(state, meta, [updated]);
    const activeSelected = state.active?.copyId === id;
    meta.stores[activeSelected ? STORE : ALTERNATIVES].put(updated);
    meta.stores[RESOLUTIONS].put(resolution);
    if (!isCurrent()) throw new Error('Receipt review is no longer active. Nothing was changed.');
    return { active: applyEvidence(activeSelected ? updated : state.active, resolution.evidence),
      alternatives: state.alternatives.map((record) => applyEvidence(record.copyId === id ? updated : record, resolution.evidence)), resolution };
  });
}

export function legacyFailurePhotoCopyId(value) {
  return `legacy-failure:${tabPhotoCopyId(value)}`;
}

export function tabPhotoCopyId(tab) {
  if (!tab || typeof tab.id !== 'string' || !tab.id.trim() || !Number.isSafeInteger(tab.revision) || tab.revision < 0) throw new Error('The running-tab copy needs an exact identity.');
  return `tab:${JSON.stringify([tab.id, tab.revision])}`;
}

// These primitives deliberately do not acquire a second browser lock. The
// recovery coordinator owns one shared operation context around the full action.
function resolvePhotoRecovery(principal, orderId, kind, operation, options) {
  const { expectedActiveRevision, expectedResolutionRevision, tab, legacyFailure, isCurrent = () => true } = options;
  const tabId = tab ? tabPhotoCopyId(tab) : null;
  const tabFacts = tab ? { fields: persistentPhotoFields(tab.fields), recovery: persistentPhotoRecovery(tab.recovery) } : null;
  if (tabFacts && !matchesKind(tabFacts.recovery.attempt, kind)) throw new Error('Saved attempt belongs to a different delivery operation.');
  let legacyRecord;
  if (legacyFailure) {
    if (operation !== 'retain' || kind !== 'confirmation' || typeof legacyFailure.attempted !== 'boolean'
        || !['', 'nobody_home', 'wrong_address', 'other'].includes(legacyFailure.fields?.reason)
        || typeof legacyFailure.fields.details !== 'string') throw new Error('Older failure facts need exact recovery review.');
    const id = legacyFailurePhotoCopyId(legacyFailure);
    legacyRecord = { copyId: id, revision: id, legacyAttempted: legacyFailure.attempted,
      fields: persistentPhotoFields({ failureReason: legacyFailure.fields.reason, failureDetails: legacyFailure.fields.details }), recovery: null };
  }
  return recoveryTransaction(principal, orderId, kind, 'readwrite', (state, meta) => {
    if (!isCurrent()) throw new Error('Recovery selection is no longer active. Nothing was changed.');
    if ((state.active?.revision ?? null) !== expectedActiveRevision || (state.resolution?.revision ?? null) !== expectedResolutionRevision) throw conflict();
    if (tabFacts?.recovery?.attempt && state.resolution?.evidence.some((item) => item.abandoned
        && attemptKey(item) === attemptKey(tabFacts.recovery.attempt))) throw conflict();
    let active = state.active;
    let alternatives = [...state.alternatives];
    const altKey = (id) => JSON.stringify([principal, orderId, kind, id]);
    const putAlternative = (record, source) => {
      const id = copyId(record);
      if (alternatives.some((item) => item.copyId === id)) return;
      const retained = { ...record, key: altKey(id), version: RECORD_VERSION, copyId: id, kind, source };
      meta.stores[ALTERNATIVES].put(retained); alternatives.push(retained);
    };
    const tabRecord = tabFacts ? { ...tabFacts, copyId: tabId, revision: tabId } : null;
    const tabAlreadyDurable = !!tabId && (active?.copyId === tabId || alternatives.some((item) => item.copyId === tabId));
    const resolution = nextResolution(state, meta, [tabRecord, legacyRecord].filter(Boolean));
    if (operation === 'discard' && tabRecord && !tabAlreadyDurable) throw new Error('Save running-tab work before discarding a durable copy.');
    if (operation !== 'discard' && tabRecord && active?.copyId !== tabId) putAlternative(tabRecord, 'tab');
    if (legacyRecord) putAlternative(legacyRecord, 'legacy-failure');
    if (operation === 'retain') {
      resolution.selections[kind] = null;
    } else if (operation === 'select') {
      const targetId = options.copyId === 'tab' ? tabId : options.copyId === 'active' ? active?.copyId : options.copyId;
      const selected = applyEvidence(active?.copyId === targetId ? active : alternatives.find((item) => item.copyId === targetId), resolution.evidence);
      if (!selected) throw conflict();
      if (selected.source === 'legacy-failure' || resolution.legacyFailureHeld || alternatives.some((record) => record.source === 'legacy-failure')
          || resolution.evidence.some((item) => item.abandoned ? selected.recovery?.attempt && attemptKey(item) === attemptKey(selected.recovery.attempt)
            : !item.accepted && (!selected.recovery?.attempt || attemptKey(item) !== attemptKey(selected.recovery.attempt)))) throw conflict();
      if (active && active.copyId !== targetId) putAlternative(active, 'device');
      meta.stores[ALTERNATIVES].delete(altKey(targetId));
      alternatives = alternatives.filter((item) => item.copyId !== targetId);
      active = { key: meta.activeKey, version: RECORD_VERSION, copyId: targetId, revision: crypto.randomUUID(),
        fields: persistentPhotoFields(selected.fields), recovery: persistentPhotoRecovery(selected.recovery) };
      meta.stores[STORE].put(active); resolution.selections[kind] = targetId;
    } else if (operation === 'discard') {
      const targetId = options.copyId === 'active' ? active?.copyId : options.copyId;
      if (!targetId) throw conflict();
      const selected = applyEvidence(active?.copyId === targetId ? active : alternatives.find((item) => item.copyId === targetId), resolution.evidence);
      if (!selected || selected.legacyAttempted || (selected.recovery?.attempt && selected.recovery.attempt.status !== 'accepted'
          && !(kind === 'confirmation' && selected.recovery.attempt.kind === 'confirmation' && options.allowUnverified === true))) throw conflict();
      if (active?.copyId === targetId) {
        meta.stores[STORE].delete(meta.activeKey); active = null;
        if (alternatives.length) resolution.selections[kind] = null;
        else delete resolution.selections[kind];
      } else {
        if (!alternatives.some((item) => item.copyId === targetId)) throw conflict();
        meta.stores[ALTERNATIVES].delete(altKey(targetId));
        alternatives = alternatives.filter((item) => item.copyId !== targetId);
        if (!active && !alternatives.length) delete resolution.selections[kind];
      }
      // TCA-26102502: explicit deletion abandons only this last readable copy.
      // Keep its identity to refuse revival by stale tabs; never retire a sibling's hold.
      const deletedAttempt = selected.recovery?.attempt;
      if (deletedAttempt && deletedAttempt.status !== 'accepted'
          && ![...meta.records.filter((record) => record.key !== meta.activeKey), active, ...alternatives]
            .some((record) => record?.recovery?.attempt && attemptKey(record.recovery.attempt) === attemptKey(deletedAttempt))) {
        resolution.evidence = resolution.evidence.map((item) => attemptKey(item) === attemptKey(deletedAttempt)
          ? { ...item, abandoned: true } : item);
      }
    }
    if (!isCurrent()) throw new Error('Recovery selection is no longer active. Nothing was changed.');
    meta.stores[RESOLUTIONS].put(resolution);
    return { active, alternatives, resolution };
  });
}

export const retainPhotoRecovery = (principal, orderId, kind, options) => resolvePhotoRecovery(principal, orderId, kind, 'retain', options);
export const selectPhotoRecovery = (principal, orderId, kind, options) => resolvePhotoRecovery(principal, orderId, kind, 'select', options);
export const discardPhotoRecovery = (principal, orderId, kind, options) => resolvePhotoRecovery(principal, orderId, kind, 'discard', options);

export function photoDraftHasWork(record, kind) {
  if (!record) return false;
  const fields = record.fields; const baseline = record.recovery?.checkpoint?.baselineText;
  if (kind === 'confirmation') return !!record.recovery?.attempt || confirmationFieldsHaveContent(fields);
  return !!record.recovery?.attempt || !!fields?.photos?.length || !!fields?.video
    || !baseline || ['receivedBy', 'receivedByRelationship', 'notes'].some((key, index) =>
      (fields?.[key] || '').trim() !== baseline[['received_by', 'received_by_relationship', 'notes'][index]]);
}

export function confirmationFieldsHaveContent(fields = {}) {
  return !!(fields.photos?.length || fields.video || fields.signatureData || fields.gpsLocation || fields.deliveryTime
    || fields.deliveryTimeIso || ['receivedBy', 'receivedByRelationship', 'notes', 'failureReason', 'failureDetails']
      .some((key) => fields[key]?.trim()));
}

// Compare actual bytes, not file names, sizes, photo counts or JSON-stringified Blobs.
const blobBytes = (blob) => typeof blob.arrayBuffer === 'function' ? blob.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(blob);
});
async function equalFacts(left, right) {
  if (left === right) return true;
  if (left instanceof Blob || right instanceof Blob) {
    if (!(left instanceof Blob) || !(right instanceof Blob) || left.size !== right.size || left.type !== right.type) return false;
    // Bound comparison memory even for a retained video or full-size originals.
    for (let offset = 0; offset < left.size; offset += 1024 * 1024) {
      const [a, b] = await Promise.all([blobBytes(left.slice(offset, offset + 1024 * 1024)), blobBytes(right.slice(offset, offset + 1024 * 1024))]);
      const rightBytes = new Uint8Array(b);
      if (!new Uint8Array(a).every((value, index) => value === rightBytes[index])) return false;
    }
    return true;
  }
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  for (const key of keys) if (!Object.hasOwn(right, key) || !await equalFacts(left[key], right[key])) return false;
  return true;
}
export const samePhotoFields = (left, right) => equalFacts(persistentPhotoFields(left), persistentPhotoFields(right));
export const samePhotoSnapshot = async (work, record) => !!record && await samePhotoFields(work.fields, record.fields)
  && (work.attempt ? attemptSignature(work.attempt) : null) === (record.recovery?.attempt ? attemptSignature(record.recovery.attempt) : null)
  && JSON.stringify(persistentPhotoRecovery({ checkpoint: work.checkpoint }).checkpoint)
    === JSON.stringify(persistentPhotoRecovery(record.recovery).checkpoint);

// Exact accepted confirmation only. Read bytes outside the transaction, then
// recheck every revision under the caller's order lock before atomic retirement.
export async function retireVerifiedPhotoCopies(principal, orderId, kind, options) {
  const { copyId: selectedId = 'active', expectedCopyRevision, isCurrent = () => true } = options;
  const saved = await readPhotoRecovery(principal, orderId, kind);
  const target = selectedId === 'active' ? saved.active : saved.alternatives.find((copy) => copy.copyId === selectedId);
  if (!target || target.revision !== expectedCopyRevision || !isCurrent()) throw conflict();
  const attempt = target.recovery?.attempt;
  if (kind !== 'confirmation' || attempt?.kind !== 'confirmation' || attempt.status !== 'accepted'
      || !attempt.receiptPhases?.core || (attempt.manifest.video && !attempt.receiptPhases.video)) throw conflict();
  const selectedFields = selectDeliveryPhotoBytes(target.fields);
  const uploadedFields = Object.fromEntries(Object.keys(attempt.fields).map((key) => [key, selectedFields[key]]));
  if (!await equalFacts(uploadedFields, attempt.fields)) throw conflict();
  const matches = [];
  for (const copy of [saved.active, ...saved.alternatives].filter(Boolean)) {
    if (copy.recovery?.attempt && attemptSignature(copy.recovery.attempt) === attemptSignature(attempt)
        && await samePhotoFields(copy.fields, target.fields)) matches.push(copy);
  }
  return recoveryTransaction(principal, orderId, kind, 'readwrite', (state, meta) => {
    if (!isCurrent() || state.active?.revision !== saved.active?.revision
        || state.resolution?.revision !== saved.resolution?.revision) throw conflict();
    for (const match of matches) {
      const actual = [state.active, ...state.alternatives].find((copy) => copy?.key === match.key);
      if (!actual || actual.revision !== match.revision) throw conflict();
      meta.stores[match.key === meta.activeKey ? STORE : ALTERNATIVES].delete(match.key);
    }
    const removed = new Set(matches.map((copy) => copy.key));
    const active = removed.has(state.active?.key) ? null : state.active;
    const alternatives = state.alternatives.filter((copy) => !removed.has(copy.key));
    const resolution = nextResolution(state, meta);
    if (!active) { if (alternatives.length) resolution.selections[kind] = null; else delete resolution.selections[kind]; }
    meta.stores[RESOLUTIONS].put(resolution);
    if (!isCurrent()) throw conflict();
    return { active, alternatives, resolution };
  });
}

// Enumerate keys first: never read another principal's blobs. Return only
// navigation facts, not media or authority; the form reloads the selected draft.
export async function listPhotoDrafts(principal) {
  photoDraftKey(principal, 'validation', 'confirmation');
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE, ALTERNATIVES], 'readonly');
      const summaries = new Map(); let failure;
      tx.oncomplete = () => resolve([...summaries.values()]);
      tx.onabort = () => reject(failure || tx.error || new Error('Saved photo work could not be read.'));
      const scan = (name) => {
      const store = tx.objectStore(name);
      const cursor = store.openKeyCursor();
      cursor.onsuccess = () => {
        const entry = cursor.result;
        if (!entry) return;
        let key;
        try { key = JSON.parse(entry.key); } catch { entry.continue(); return; }
        if (!Array.isArray(key) || key[0] !== principal) { entry.continue(); return; }
        const read = store.get(entry.key);
        read.onsuccess = () => {
          try {
            const record = read.result;
            if (key.length !== (name === STORE ? 3 : 4) || (name === STORE && photoDraftKey(...key) !== entry.key) || !compatible(record?.version)
                || record.key !== entry.key || typeof record.revision !== 'string') throw new Error('A saved photo draft needs a compatible app. Nothing was changed.');
            photoDraftKey(key[0], key[1], key[2]);
            if (name === ALTERNATIVES && (record.kind !== key[2] || record.copyId !== key[3])) throw new Error('Saved copy identity needs review.');
            const recovery = restorePhotoRecovery(record.recovery);
            if (!matchesKind(recovery.attempt, key[2])) throw new Error('Saved operation identity needs review.');
            if (name === ALTERNATIVES || photoDraftHasWork(record, key[2])) {
              const identity = JSON.stringify([key[1], key[2]]); const previous = summaries.get(identity);
              const summary = { orderId: key[1], kind: key[2], revision: record.revision,
                status: recovery.attempt?.status || 'draft', ...(recovery.attempt?.kind === 'failure' ? { operation: 'failure' } : {}) };
              const alternativeCount = (previous?.alternativeCount || 0) + (name === ALTERNATIVES ? 1 : 0);
              summaries.set(identity, { ...(name === STORE || !previous ? summary : previous),
                ...(alternativeCount ? { alternativeCount, needsRecovery: true } : {}) });
            }
            entry.continue();
          } catch (error) { failure = error; tx.abort(); }
        };
      };
      };
      scan(STORE); scan(ALTERNATIVES);
    });
  } finally { db.close(); }
}
